import { getDataClient } from "@/lib/dataClient";
import { useDataQuery } from "@/hooks/useDataQuery";

// Yönetici paneli — paket, destek talebi ve denetim kaydı veri katmanı.

export interface Package {
  id: string;
  productCode: string;
  name: string;
  description: string;
  price: number;
  durationDays: number;
  annualEnabled: boolean;
  annualDiscountPercent: number;
  merakliEnabled: boolean;
  meetingEnabled: boolean;
  voiceCoach: boolean;
  parentalControls: boolean;
  offlineMemory: boolean;

  features: string[];
  active: boolean;
  sortOrder: number;
}

export function usePackages() {
  return useDataQuery<Package[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("packages")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []).map(
      (p: { id: string; product_code?:string; name: string; description: string | null; price: number | null; duration_days: number | null; features: string[] | null; active: boolean; sort_order: number | null; annual_enabled?: boolean; annual_discount_percent?: number; merakli_enabled?: boolean; meeting_enabled?: boolean; voice_coach?: boolean; parental_controls?: boolean; offline_memory?: boolean }) => ({
        id: p.id,
        productCode:p.product_code??"",
        name: p.name,
        description: p.description ?? "",
        price: Number(p.price ?? 0),
        durationDays: Number(p.duration_days ?? 0),
        features: Array.isArray(p.features) ? p.features : [],
        active: p.active,
        sortOrder: Number(p.sort_order ?? 0),
        annualEnabled:!!p.annual_enabled,annualDiscountPercent:Number(p.annual_discount_percent??0),
        merakliEnabled:!!p.merakli_enabled,meetingEnabled:!!p.meeting_enabled,
        voiceCoach:!!p.voice_coach,parentalControls:!!p.parental_controls,offlineMemory:!!p.offline_memory,
      }),
    );
  });
}

export interface PackageInput {
  name: string;
  description: string;
  price: number;
  durationDays: number;
  annualEnabled: boolean;
  annualDiscountPercent: number;
  merakliEnabled: boolean;
  meetingEnabled: boolean;
  voiceCoach: boolean;
  parentalControls: boolean;
  offlineMemory: boolean;

  features: string[];
  active: boolean;
}

export async function createPackage(input: PackageInput): Promise<string | null> {
  const dataClient = getDataClient();
  const { error } = await dataClient.from("packages").insert({
    name: input.name,
    description: input.description,
    price: input.price,
    duration_days: input.durationDays,
    features: input.features,
    annual_enabled:input.annualEnabled,annual_discount_percent:input.annualDiscountPercent,
    merakli_enabled:input.merakliEnabled,meeting_enabled:input.meetingEnabled,
    voice_coach:input.voiceCoach,parental_controls:input.parentalControls,offline_memory:input.offlineMemory,
    active: input.active,
    sort_order: 0,
  });
  return error ? error.message : null;
}

export async function updatePackage(
  id: string,
  input: PackageInput,
): Promise<string | null> {
  const dataClient = getDataClient();
  const { error } = await dataClient
    .from("packages")
    .update({
      name: input.name,
      description: input.description,
      price: input.price,
      duration_days: input.durationDays,
      features: input.features,
    annual_enabled:input.annualEnabled,annual_discount_percent:input.annualDiscountPercent,
    merakli_enabled:input.merakliEnabled,meeting_enabled:input.meetingEnabled,
    voice_coach:input.voiceCoach,parental_controls:input.parentalControls,offline_memory:input.offlineMemory,
      active: input.active,
    })
    .eq("id", id);
  return error ? error.message : null;
}

export async function deletePackage(id: string): Promise<string | null> {
  const dataClient = getDataClient();
  const { error } = await dataClient.from("packages").delete().eq("id", id);
  return error ? error.message : null;
}

export interface SupportTicket {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: string;
  createdAt: string;
}

export const TICKET_STATUS_LABELS: Record<string, string> = {
  yeni: "Yeni",
  inceleniyor: "İnceleniyor",
  cozuldu: "Çözüldü",
  kapatildi: "Kapatıldı",
};

export const TICKET_STATUS_COLORS: Record<string, string> = {
  yeni: "bg-accent-100 text-accent-800",
  inceleniyor: "bg-secondary-100 text-secondary-900",
  cozuldu: "bg-primary-100 text-primary-800",
  kapatildi: "bg-background-100 text-foreground-500",
};

export function useSupportTickets() {
  return useDataQuery<SupportTicket[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("support_tickets")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(
      (t: { id: string; name: string | null; email: string | null; subject: string | null; message: string | null; status: string | null; created_at: string }) => ({
        id: t.id,
        name: t.name ?? "—",
        email: t.email ?? "—",
        subject: t.subject ?? "—",
        message: t.message ?? "",
        status: t.status ?? "yeni",
        createdAt: t.created_at,
      }),
    );
  });
}

export async function updateTicketStatus(id: string, status: string): Promise<string | null> {
  const dataClient = getDataClient();
  const { error } = await dataClient
    .from("support_tickets")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", id);
  return error ? error.message : null;
}

export interface AuditLogEntry {
  id: string;
  userName: string;
  action: string;
  entity: string;
  details: string;
  createdAt: string;
}

export function useAuditLog() {
  return useDataQuery<AuditLogEntry[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    return (data ?? []).map(
      (l: { id: string; user_name: string | null; action: string | null; entity: string | null; details: string | null; created_at: string }) => ({
        id: l.id,
        userName: l.user_name ?? "Yönetici",
        action: l.action ?? "—",
        entity: l.entity ?? "—",
        details: l.details ?? "",
        createdAt: l.created_at,
      }),
    );
  });
}

// Yönetici işlemlerini denetim kaydına yazar (yalnızca admin; RPC güvenlik kontrolü yapar).
export async function logAudit(action: string, entity: string, details: string): Promise<void> {
  try {
    const dataClient = getDataClient();
    await dataClient.rpc("admin_log_audit", {
      p_action: action,
      p_entity: entity,
      p_details: details,
    });
  } catch {
    // Denetim kaydı başarısız olsa bile ana işlem etkilenmez.
  }
}